import React from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import {
  HomeOutlined,
  MessageOutlined,
  UsergroupDeleteOutlined,
  ContactsOutlined,
  LineChartOutlined
} from '@ant-design/icons';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FLAGS } from 'legacy/common/constants/launchdarkly';
import NavigationMenu from 'components/MainNavigationMenu';
import { MenuItem } from 'components/MainNavigationMenu/types';
import { FaAddressCard } from 'react-icons/fa';

interface Props {
  isAdmin: boolean;
  isUserMember?: boolean;
  className?: string;
}

const OrganizationNavigationMenu = ({
  isAdmin,
  isUserMember,
  className
}: Props) => {
  const { t } = useTranslation(['organizations']);
  const flags = useFlags();
  const router = useRouter();
  const { query } = router;

  const handleOrganizationRouteChange = (newPath: string) => {
    if (newPath !== '') {
      let newRoute = newPath;

      // Replace dynamic route parameters
      if (query.organizationId && typeof query.organizationId === 'string') {
        newRoute = newRoute.replace('[organizationId]', query.organizationId);
      }
      if (query.slug && typeof query.slug === 'string') {
        newRoute = newRoute.replace('[slug]', query.slug);
      }
      if (query.albumId && typeof query.albumId === 'string') {
        newRoute = newRoute.replace('[albumId]', query.albumId);
      }

      router.push(newRoute);
    }
  };

  const organizationMenuItems: MenuItem[] = [
    {
      condition: () => true,
      label: t('navigation.dashboard'),
      value: '/organizations/[organizationId]/dashboard',
      icon: <HomeOutlined />
    },
    {
      condition: () => true,
      label: t('navigation.chats'),
      value: '/organizations/[organizationId]/inbox',
      icon: <MessageOutlined />
    },
    {
      condition: () => true,
      label: t('navigation.moments'),
      values: [
        '/organizations/[organizationId]/moments',
        '/organizations/[organizationId]/moments/albums/[albumId]',
        '/organizations/[organizationId]/moments/albums/[albumId]/create'
      ],
      icon: (
        <>
          {/* using inline svg here so it can apply the style when the icon is not active - using img tag here makes the icon always be white and not visible when the menu is not active */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="25"
            height="24"
            viewBox="0 0 25 24"
            fill="currentColor"
          >
            <path
              d="M10.3 19.7778L7.55 13.6667L1.5 10.8889L7.55 8.11111L10.3 2L13.05 8.11111L19.1 10.8889L13.05 13.6667L10.3 19.7778ZM19.1 22L17.725 18.9444L14.7 17.5556L17.725 16.1667L19.1 13.1111L20.475 16.1667L23.5 17.5556L20.475 18.9444L19.1 22Z"
              fill="currentColor"
            />
          </svg>
        </>
      )
    },
    {
      condition: (isAdmin) => isAdmin,
      label: t('navigation.members'),
      value: '/organizations/[organizationId]/members',
      icon: <UsergroupDeleteOutlined />
    },
    {
      condition: (isAdmin) => isAdmin,
      label: t('navigation.memberships'),
      value: '/organizations/[organizationId]/edit/settings/memberships',
      icon: <FaAddressCard />
    },
    {
      condition: (isAdmin) => isAdmin,
      label: t('navigation.contacts'),
      value: '/organizations/[organizationId]/external-contacts',
      icon: <ContactsOutlined />
    },
    {
      condition: (isAdmin) =>
        isAdmin && flags[FLAGS.ENABLE_NEW_EVENT_DASHBOARD],
      label: t('navigation.analytics'),
      value: '/organizations/[organizationId]/analytics',
      icon: <LineChartOutlined />
    }
  ];

  return (
    <NavigationMenu
      isAdmin={isAdmin}
      isUserMember={isUserMember}
      menuItems={organizationMenuItems}
      className={className}
      onRouteChange={handleOrganizationRouteChange}
    />
  );
};

export default OrganizationNavigationMenu;
